// src/components/ForgotPasswordModal/ForgotPasswordModal.jsx
import React, { useState, useEffect } from 'react';
import { IoClose } from 'react-icons/io5'; // Using IoClose for the close button
import { get, put } from '../../utility/fetch';
import notification from '../../utility/notification';

// import './ForgotPasswordModal.css'; // We'll create this CSS file next

// Step Components (will be defined below)
const EmailStep = ({ onNext, setEmail, email, isLoading }) => {
  const [localEmail, setLocalEmail] = useState(email);
  const [error, setError] = useState('');

  const validateEmail = (email) => {
    // Basic email regex
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const handleSubmit = () => {
    if (!localEmail) {
      setError('Email cannot be empty.');
      return;
    }
    if (!validateEmail(localEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    setEmail(localEmail);
    onNext();
  };

  return (
    <div className="modal-step">
      <h2>Forgot Password</h2>
      <p>Enter your email to receive a One-Time Password (OTP).</p>
      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <input
          type="email"
          id="email"
          value={localEmail}
          onChange={(e) => { setLocalEmail(e.target.value); setError(''); }}
          placeholder="your@example.com"
          className={error ? 'input-error' : ''}
        />
        {error && <p className="error-message">{error}</p>}
      </div>
      <button className="button-modal"  onClick={handleSubmit} disabled={isLoading}>
        {isLoading ? 'Sending OTP...' : 'Send OTP'}
      </button>
    </div>
  );
};

const OtpStep = ({ onNext, onBack, otp, setOtp, onResendOtp, isLoading, resendTimer }) => {
  const [localOtp, setLocalOtp] = useState(otp);
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!localOtp || localOtp.length !== 6) { // Assuming 6-digit OTP
      setError('Please enter a 6-digit OTP.');
      return;
    }
    setError('');
    setOtp(localOtp);
    onNext();
  };

  return (
    <div className="modal-step">
      <h2>Enter OTP</h2>
      <p>A 6-digit OTP has been sent to your email.</p>
      <div className="form-group">
        <label htmlFor="otp">One-Time Password</label>
        <input
          type="text"
          id="otp"
          value={localOtp}
          onChange={(e) => { setLocalOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }} // Only numbers, max 6
          placeholder="Enter OTP"
          className={error ? 'input-error' : ''}
          maxLength="6"
        />
        {error && <p className="error-message">{error}</p>}
      </div>
      <div className="otp-actions">
        {resendTimer > 0 ? (
          <p>Resend OTP in {resendTimer}s</p>
        ) : (
          <button onClick={onResendOtp} disabled={isLoading} className="button-modal resend-btn">
            {isLoading ? 'Sending...' : 'Resend OTP'}
          </button>
        )}
      </div>
      <div className="modal-actions">
        <button onClick={onBack} className="back-btn button-modal">Back</button>
        <button className='button-modal' onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Verifying...' : 'Verify OTP'}
        </button>
      </div>
    </div>
  );
};

const ChangePasswordStep = ({ onNext, onBack, newPassword, closeSideBar, setNewPassword, confirmPassword, setConfirmPassword, isLoading }) => {
  const [localNewPassword, setLocalNewPassword] = useState(newPassword);
  const [localConfirmPassword, setLocalConfirmPassword] = useState(confirmPassword);
  const [errors, setErrors] = useState({});

  // Password complexity checks
  const checkComplexity = (password) => {
    const issues = {};
    if (password.length < 8) issues.length = 'Minimum 8 characters';
    if (!/[A-Z]/.test(password)) issues.uppercase = 'At least one uppercase letter';
    if (!/[a-z]/.test(password)) issues.lowercase = 'At least one lowercase letter';
    if (!/\d/.test(password)) issues.number = 'At least one number';
    if (!/[!@#$%^&*()]/.test(password)) issues.symbol = 'At least one special character (!@#$%^&*())';
    return issues;
  };

  const handleSubmit = () => {
    const passwordIssues = checkComplexity(localNewPassword);
    const newErrors = { ...passwordIssues };

    if (localNewPassword !== localConfirmPassword) {
      newErrors.match = 'Passwords do not match.';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setNewPassword(localNewPassword);
    setConfirmPassword(localConfirmPassword);
    onNext(); // This will trigger the final submit in the parent
  };

  const passwordComplexityIssues = checkComplexity(localNewPassword);

  return (
    <div className="modal-step">
      <h2>Change Password</h2>
      <p>Set your new password.</p>
      <div className="form-group">
        <label htmlFor="newPassword">New Password</label>
        <input
          type="password"
          id="newPassword"
          value={localNewPassword}
          onChange={(e) => { setLocalNewPassword(e.target.value); setErrors({}); }}
          placeholder="Enter new password"
          className={errors.length || errors.uppercase || errors.lowercase || errors.number || errors.symbol ? 'input-error' : ''}
        />
        {Object.keys(passwordComplexityIssues).length > 0 && localNewPassword.length > 0 && (
          <ul className="complexity-feedback">
            {Object.values(passwordComplexityIssues).map((msg, idx) => (
              <li key={idx} className="issue">{msg}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="form-group">
        <label htmlFor="confirmPassword">Confirm New Password</label>
        <input
          type="password"
          id="confirmPassword"
          value={localConfirmPassword}
          onChange={(e) => { setLocalConfirmPassword(e.target.value); setErrors({}); }}
          placeholder="Confirm new password"
          className={errors.match ? 'input-error' : ''}
        />
        {errors.match && <p className="error-message">{errors.match}</p>}
      </div>
      <div className="modal-actions">
        <button onClick={onBack} className="back-btn button-modal">Back</button>
        <button className='button-modal' onClick={handleSubmit} disabled={isLoading || Object.keys(passwordComplexityIssues).length > 0 || localNewPassword !== localConfirmPassword}>
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </button>
      </div>
    </div>
  );
};


const ForgotPasswordModal = ({ isOpen, closeSideBar, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0); // Timer for OTP resend

  // Reset all states when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setEmail('');
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
      setIsLoading(false);
      setResendTimer(0);
    }
  }, [isOpen]);

  // OTP Timer useEffect
  useEffect(() => {
    let timerInterval;
    if (resendTimer > 0) {
      timerInterval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timerInterval);
  }, [resendTimer]);


  const handleNextStep = async () => {
    setIsLoading(true);
    try {
      if (currentStep === 1) {
        // Simulate API call for sending OTP
        console.log(`Sending OTP to: ${email}`);
        let res = await get(`/auth/forget-password/${email}`); // Simulate network delay
        
        console.log(res);

        setResendTimer(60); // Start timer after sending OTP
        setCurrentStep(2);
      } else if (currentStep === 2) {
        // Simulate API call for verifying OTP
        console.log(`Verifying OTP: ${otp} for email: ${email}`);
        let res = await get(`/healthCareProvider/validateotp?otp=${otp}&email=${email}`); 
        console.log(res);
        if(res){
            setCurrentStep(3);
        }
        // Simulate network delay
        // In a real app: call your backend API here
        // const response = await api.post('/verify-otp', { email, otp });
        // if (response.success) { ... } else { throw new Error('API error'); }
      } else if (currentStep === 3) {
        // Simulate API call for changing password
        console.log(`Changing password for ${email}: ${newPassword}`);
        let res = await put(`/auth/change-employee-password/${email}`, {password: newPassword})
       if(res){
        notification({
            type: 'success',
            message: 'Password changed successfully!',
        })
            closeSideBar();
       }
        console.log(res);

        onClose(); // Close modal on success
      }
    } catch (error) {
      console.error("Forgot password process error:", error);
      // You'd typically set an error message state here to display to the user
      alert('An error occurred. Please try again.');
      // If error is specific to a step, you might revert step or show specific message
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      console.log(`Resending OTP to: ${email}`);
      await new Promise((resolve) => setTimeout(resolve, 1500)); // Simulate network delay
      // In a real app: call your backend API here to resend OTP
      setResendTimer(60); // Restart timer
      alert('New OTP sent!');
    } catch (error) {
      console.error("Error resending OTP:", error);
      alert('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleBackStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (!isOpen) {
    return null; // Don't render modal when it's closed
  }

  const isMobile = window.innerWidth < 768;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={isMobile ? { width: '90%', padding: '20px' } : {}}>
        <button className="close-button " onClick={onClose}>
          <IoClose color='red'/>
        </button>

        {currentStep === 1 && (
          <EmailStep
            onNext={handleNextStep}
            setEmail={setEmail}
            email={email}
            isLoading={isLoading}
          />
        )}
        {currentStep === 2 && (
          <OtpStep
            onNext={handleNextStep}
            onBack={handleBackStep}
            otp={otp}
            setOtp={setOtp}
            onResendOtp={handleResendOtp}
            isLoading={isLoading}
            resendTimer={resendTimer}
          />
        )}
        {currentStep === 3 && (
          <ChangePasswordStep
            onNext={handleNextStep}
            onBack={handleBackStep}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordModal;